import { useCallback, useEffect, useRef, useState } from "react";
import { getPydanticAIChatHeaders } from "duck/api/axios";
import { getFollowup } from "duck/endpoints/getFollowup";
import { useThreadManager } from "duck/endpoints/threads";
import { AppState } from "duck/types";
import type { ClientAction } from "duck/ui/chat/actions";
import {
  SSERequest,
  UserContentStructure,
} from "duck/ui/chat/serverSentEvents";
import { sendMessage as sendSSEMessage } from "duck/ui/chat/serverSentEvents/chatClient";
import {
  createChatEventHandler,
  createStopStreamingHandler,
} from "duck/ui/chat/serverSentEvents/messageHandler";
import { subthreadManager } from "duck/ui/chat/subthreadManager";
import { ChatMessage } from "duck/ui/chat/types";
import {
  buildChatEndpointURL,
  createAssistantMessage,
  createUserMessage,
  extractAndSetMessages,
  validateChatAttachment,
} from "duck/ui/chat/utils";
import { getAppState } from "duck/ui/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";

import { useConfigContext } from "shared/contexts/ConfigContext";
import { useViewport } from "shared/contexts/ViewportContext";
import { getUserLocale } from "shared/locale/getUserLocale";

import { toast } from "features/ui/Toast";

import { useDuckFullscreen } from "./useDuckFullscreen";
import { useDuckLinkParams } from "./useDuckLinkParams";

interface UseStreamingChatProps {
  appState?: Partial<AppState>;
}

interface RequestFollowupOptions {
  forceRefresh?: boolean;
  preserveExisting?: boolean;
}

export const useStreamingChat = ({ appState }: UseStreamingChatProps = {}) => {
  const {
    currentThreadId,
    currentThreadMessages,
    createNewThread,
    threads,
    selectThread,
    isCreating,
    isSwitchingThread,
    isLoadingCurrentThread,
    updateCurrentThreadTitle,
    deleteThread,
    listThreadMessagesRequestKey,
    setUseListThreadArgs,
    useListThreadArgs,
    isLoadingThreads,
  } = useThreadManager();

  const { t } = useTranslation("duck");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingSubthreadCount, setStreamingSubthreadCount] = useState(0);
  const isAnySubthreadStreaming = streamingSubthreadCount > 0;
  const [followupSuggestions, setFollowupSuggestions] = useState<string[]>([]);
  const [isFetchingFollowups, setIsFetchingFollowupsInternal] = useState(false);
  // Ref mirrors the state so the guard in requestFollowupSuggestions reads the
  // live value rather than the one captured when the callback was memoized.
  const isFetchingFollowupsRef = useRef(false);
  const setIsFetchingFollowups = useCallback((fetching: boolean) => {
    isFetchingFollowupsRef.current = fetching;
    setIsFetchingFollowupsInternal(fetching);
  }, []);
  const [followupQuestionsEnabled, setFollowupQuestionsEnabledInternal] =
    useState(true);
  // Ref avoids stale closure in handleStreamingEnd, which is captured at sendMessage time.
  const followupQuestionsEnabledRef = useRef(true);
  const setFollowupQuestionsEnabled = useCallback((enabled: boolean) => {
    followupQuestionsEnabledRef.current = enabled;
    setFollowupQuestionsEnabledInternal(enabled);
  }, []);
  const [safeModeSelected, setSafeMode] = useState(false);
  const [pendingClientActions, setPendingClientActions] = useState<
    ClientAction[]
  >([]);
  const activeController = useRef<ReturnType<typeof sendSSEMessage> | null>(
    null
  );
  const lastStreamThreadIdRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLElement>(null);

  const { pages } = useConfigContext();
  const { isMobile } = useViewport();
  const {
    pydanticChatFollowups: followupsEnabledFF,
    duckSafeMode: safeModeFF,
    duckChatRoute,
  } = useFlags();
  const chatRoute = duckChatRoute || "/pydanticai/chat";
  const safeMode = Boolean(safeModeFF) && safeModeSelected;
  const { isFullscreen } = useDuckFullscreen();

  useDuckLinkParams(selectThread);

  // Subscribe to global subthread streaming state
  useEffect(
    () =>
      subthreadManager.subscribeToGlobalStreaming((count) => {
        setStreamingSubthreadCount(count);
      }),
    []
  );

  const handleClientAction = useCallback((action: ClientAction) => {
    setPendingClientActions((prev) => [...prev, action]);
  }, []);

  const requestFollowupSuggestions = useCallback(
    async (threadId?: string | null, options: RequestFollowupOptions = {}) => {
      const { forceRefresh = false, preserveExisting = false } = options;

      if (!threadId) {
        return;
      }

      if (isFetchingFollowupsRef.current) {
        return;
      }

      if (!preserveExisting) {
        setFollowupSuggestions([]);
      }

      setIsFetchingFollowups(true);

      try {
        const followups = await getFollowup({
          threadId,
          forceRefresh,
        });

        // Only update if we're still on the same thread
        if (currentThreadId === threadId) {
          setFollowupSuggestions(followups);
        }
      } catch (error) {
        console.error("Failed to fetch follow-up suggestions:", error);
        if (currentThreadId === threadId && !preserveExisting) {
          setFollowupSuggestions([]);
        }
      } finally {
        setIsFetchingFollowups(false);
      }
    },
    [currentThreadId, setIsFetchingFollowups]
  );

  const refreshFollowupSuggestions = useCallback(async () => {
    if (!currentThreadId) {
      return;
    }

    await requestFollowupSuggestions(currentThreadId, {
      forceRefresh: true,
      preserveExisting: true,
    });
  }, [currentThreadId, requestFollowupSuggestions]);

  // Initialize messages when the selected thread changes or when messages are loaded.
  // Avoid resync at stream end which can cause flicker.
  const prevThreadIdRef = useRef<string | null>(null);
  const prevStreamingRef = useRef<boolean>(false);

  // Sync messages when thread changes or messages are loaded
  useEffect(() => {
    const currentId = currentThreadId ?? null;
    const wasStreaming = prevStreamingRef.current;
    prevStreamingRef.current = isStreaming;

    // Thread changed - update messages when available
    if (currentId && prevThreadIdRef.current !== currentId) {
      prevThreadIdRef.current = currentId;
      // Only clear messages if we're not currently streaming
      if (!isStreaming) {
        // Clear followup suggestions and reset client action state when switching threads
        setFollowupSuggestions([]);
        setIsFetchingFollowups(false);
        setFollowupQuestionsEnabled(true);
        setPendingClientActions([]);
        setMessages([]);
        // Messages might not be loaded yet, will be set when currentThreadMessages updates
        if (currentThreadMessages) {
          extractAndSetMessages(
            currentThreadMessages,
            isStreaming,
            setMessages
          );
          // Request followups for the loaded thread
          // followupQuestionsEnabled is always true here (just reset above)
          if (followupsEnabledFF && !isMobile) {
            requestFollowupSuggestions(currentId);
          }
        }
      }
    }
    // Same thread but messages just loaded or updated
    // Only sync when NOT streaming AND we weren't just streaming
    // This prevents overwriting local optimistic updates during/after message sending
    else if (
      currentId &&
      currentThreadMessages &&
      !isStreaming &&
      !wasStreaming
    ) {
      extractAndSetMessages(currentThreadMessages, isStreaming, setMessages);
    }

    if (!currentId) {
      prevThreadIdRef.current = null;
      setFollowupSuggestions([]);
      setIsFetchingFollowups(false);
      setFollowupQuestionsEnabled(true);
      setPendingClientActions([]);
    }
  }, [
    currentThreadId,
    currentThreadMessages,
    isStreaming,
    isMobile,
    followupsEnabledFF,
    requestFollowupSuggestions,
    setFollowupQuestionsEnabled,
    setIsFetchingFollowups,
  ]);

  // On stream end refresh metadata and collect follow-up suggestions
  const handleStreamingEnd = useCallback(
    async (threadId?: string | null) => {
      const targetThreadId = threadId ?? currentThreadId;
      try {
        await updateCurrentThreadTitle(targetThreadId ?? undefined);
      } catch (error) {
        console.error("Failed to update thread title:", error);
      }

      // mutate messages to sync with backend (ensures messages have proper IDs, which we need for Feedback to work!)
      if (listThreadMessagesRequestKey) {
        await mutate(listThreadMessagesRequestKey);
      }

      if (
        followupsEnabledFF &&
        followupQuestionsEnabledRef.current &&
        !isMobile
      ) {
        await requestFollowupSuggestions(targetThreadId);
      }
    },
    [
      currentThreadId,
      isMobile,
      followupsEnabledFF,
      listThreadMessagesRequestKey,
      requestFollowupSuggestions,
      updateCurrentThreadTitle,
    ]
  );

  const sendMessage = async (
    text: string,
    newThread?: boolean,
    attachments?: File[],
    safeModeOverride?: boolean
  ): Promise<boolean> => {
    if (!text.trim()) return false;

    // The override lets callers (e.g. "Test in safe mode" on the Directives
    // page) send safely in the same tick they call setSafeMode, before the
    // state update is reflected in this closure.
    const messageSafeMode =
      Boolean(safeModeFF) && (safeModeOverride ?? safeModeSelected);

    const activeAttachments = attachments?.filter(Boolean) ?? [];
    for (const file of activeAttachments) {
      const validation = validateChatAttachment(file);
      if (!validation.isValid) {
        toast.error(t(validation.errorKey!));

        return false;
      }
    }
    const optimisticAttachmentJsons = activeAttachments
      .map(({ name, type }): UserContentStructure | null => {
        const mediaType = type || "text/plain";
        const uploadDescription = `The user uploaded the file named ${name} of type ${mediaType}`;
        try {
          return {
            type: "text",
            text: JSON.stringify({
              upload_description: uploadDescription,
              file_name: name,
              media_type: mediaType,
            }),
          };
        } catch {
          return null;
        }
      })
      .filter((attachment): attachment is UserContentStructure =>
        Boolean(attachment)
      );

    // Prepare user + placeholder assistant message
    const userMessage = createUserMessage(
      text,
      messageSafeMode,
      optimisticAttachmentJsons
    );
    const assistantMessage = createAssistantMessage(messageSafeMode);

    let activeThreadId: string;
    const needsNewThread = newThread || !currentThreadId;
    if (needsNewThread) {
      // Start a clean UX for the new thread: lock streaming and clear messages first
      if (newThread) {
        setMessages([]);
      }

      setFollowupQuestionsEnabled(true);
      setPendingClientActions([]);

      // Set streaming BEFORE creating thread to prevent useEffect from clearing messages
      setIsStreaming(true);

      try {
        activeThreadId = await createNewThread();
      } catch (error) {
        console.error("Failed to create thread:", error);
        setIsStreaming(false);

        return false;
      }
    } else {
      activeThreadId = currentThreadId;
      setIsStreaming(true);
    }

    setFollowupSuggestions([]);
    setIsFetchingFollowups(false);

    lastStreamThreadIdRef.current = activeThreadId;

    // Add messages locally if not already added
    setMessages((prev) => [...prev, userMessage, assistantMessage]);

    // Send message to chat endpoint
    const input: SSERequest = {
      thread_id: activeThreadId,
      message: text.trim(),
      app_state: {
        ...getAppState(pages, isFullscreen),
        ...appState,
      },
      message_attachments: activeAttachments,
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      user_locale: getUserLocale(),
      viewport_is_mobile: isMobile,
    };

    // Create event handler for processing SSE events
    const eventHandler = createChatEventHandler({
      t,
      assistantMessageId: assistantMessage.id,
      setMessages,
      setIsStreaming,
      onStreamingEnd: () => handleStreamingEnd(activeThreadId),
    });

    const controller = sendSSEMessage({
      url: buildChatEndpointURL(chatRoute, messageSafeMode),
      input,
      headers: getPydanticAIChatHeaders(),
      onEvent: eventHandler,
      onClientAction: handleClientAction,
      safeMode: messageSafeMode,
      t,
    });

    activeController.current = controller;

    return true;
  };

  // Create stop streaming handler
  const stopStreamingHandler = createStopStreamingHandler({
    t,
    setMessages,
    setIsStreaming,
    activeController,
    onStreamingEnd: () => handleStreamingEnd(lastStreamThreadIdRef.current),
  });

  const stopStreaming = () => {
    if (!isStreaming) return;

    stopStreamingHandler();
  };

  const handleCreateNewThread = useCallback(async (): Promise<string> => {
    // Reset chat state — identical whether we reuse an empty thread or create one.
    setMessages([]);
    lastStreamThreadIdRef.current = null;
    setFollowupSuggestions([]);
    setIsFetchingFollowups(false);
    setFollowupQuestionsEnabled(true);
    setPendingClientActions([]);

    // Check if there's already an empty thread (no messages)
    const emptyThread = threads?.find(
      (thread) => thread.last_message_at === null
    );

    if (emptyThread) {
      // Just select the existing empty thread instead of creating a new one
      selectThread(emptyThread.id, false);

      return emptyThread.id;
    }

    // No empty thread exists, create a new one
    return createNewThread();
  }, [
    threads,
    createNewThread,
    selectThread,
    setFollowupQuestionsEnabled,
    setIsFetchingFollowups,
  ]);

  const focusInput = () => inputRef.current?.focus();

  return {
    threads,
    messages,
    isStreaming,
    isAnySubthreadStreaming,
    streamingSubthreadCount,
    isCreating,
    isSwitchingThread,
    isLoadingCurrentThread,
    currentThreadId,
    followupSuggestions: followupsEnabledFF ? followupSuggestions : [],
    isFetchingFollowups: followupsEnabledFF ? isFetchingFollowups : false,
    followupQuestionsEnabled,
    setFollowupQuestionsEnabled,
    safeMode,
    setSafeMode,
    pendingClientActions,
    setPendingClientActions,
    sendMessage,
    stopStreaming,
    createNewThread: handleCreateNewThread,
    refreshFollowupSuggestions,
    selectThread,
    deleteThread,
    focusInput,
    inputRef,
    setUseListThreadArgs,
    useListThreadArgs,
    isLoadingThreads,
  };
};
